import { useCallback, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { List } from 'foundations-components/transitional/components/List';
import { INVOICE_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { InvoicePaymentStatus } from 'invoices-ui-lib/types/InvoicePaymentStatus';
import { getInvoiceCategories, hasPayments } from 'invoices-ui-lib/utils/invoice';
import { filterAndFlattenActionGroups, insertDividers } from 'invoices-ui-lib/utils/addDropdownDividers';
import InvoiceDeleteButton from 'invoices-ui-lib/components/Buttons/InvoiceDeleteButton';
import DownloadInvoiceButton from 'invoices-ui-lib/components/Buttons/DownloadInvoiceButton';
import InvoiceDeleteModal from 'invoices-ui-lib/components/Modals/InvoiceDeleteModal';
import useDeleteInvoice from 'invoices-ui-lib/hooks/useDeleteInvoice';
import { addSuccess, addDanger
// @ts-expect-error untyped-module
} from 'customer-data-ui-utilities/alerts/Alerts';
import { trackUsage } from '../utils/tracking';
import { usePaymentsEligibilityContext } from 'invoices-ui-lib/context/PaymentsEligibilityContext';
import { getInvoiceProperty } from 'invoices-ui-lib/utils/property';
import { executePDFDownload } from 'invoices-ui-lib/utils/executePDFDownload';
import { useGetQuickBooksInvoiceDownloadUrl } from 'accounting-integrations-ui-components/hooks/useGetQuickBooksInvoiceDownloadUrl';
import http from 'hub-http/clients/apiClient';
import { IntegrationAppNames } from 'accounting-integrations-ui-components/constants/IntegrationAppNames';
import { useIsRecordInSync } from 'accounting-integrations-ui-components/hooks/useIsRecordInSync';
import { actions } from 'invoices-ui-lib/constants/actions';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NonNativeInvoiceHighlightActions = props => {
  const {
    invoice,
    hasInvoiceEditPermissions,
    paymentStatus,
    closeSidebar,
    canDeleteInvoice
  } = props;
  const {
    paymentsEligibility
  } = usePaymentsEligibilityContext();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const sendInvoiceUpdatedMessage = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_OBJECT);
  const sendInvoiceDeletedMessage = useSendCrmMessageTopic(TOPIC_NAMES.OBJECT_DELETED);
  const onSuccess = useCallback(() => {
    sendInvoiceUpdatedMessage({
      objectTypeId: INVOICE_TYPE_ID,
      objectId: String(invoice.objectId)
    });
    sendInvoiceDeletedMessage({
      objectTypeId: INVOICE_TYPE_ID,
      objectId: String(invoice.objectId)
    });
    closeSidebar();
    addSuccess('invoicesHighlightCard.alerts.delete.success', {}, {
      'data-test-id': 'delete-invoice-success'
    });
  }, [closeSidebar, invoice, sendInvoiceDeletedMessage, sendInvoiceUpdatedMessage]);
  const onFailure = useCallback(() => {
    addDanger('invoicesHighlightCard.alerts.delete.danger');
  }, []);
  const invoiceHasPayments = hasPayments(invoice) || paymentStatus === InvoicePaymentStatus.PENDING;
  const {
    deleteLoading,
    onConfirmDeleteInvoice
  } = useDeleteInvoice(invoice, setIsDeleteModalOpen, onSuccess, onFailure, onFailure);
  const isNativeInvoice = getInvoiceProperty(invoice, 'hs_invoice_source') === 'native_invoice';
  const invoiceSourceDetail1 = getInvoiceProperty(invoice, 'hs_object_source_detail_1');
  const invoiceNumber = getInvoiceProperty(invoice, 'hs_number');
  const invoiceTitle = getInvoiceProperty(invoice, 'hs_title');
  const isPaymentCollectedAutomatically = getInvoiceProperty(invoice, 'hs_invoice_payment_collection_method') === 'automatic';
  const {
    url: quickBooksInvoiceDownloadUrl,
    invoiceConnectionId
  } = useGetQuickBooksInvoiceDownloadUrl();
  const {
    hasRecord: hasQBOInvoicesSyncRecord
  } = useIsRecordInSync(invoiceConnectionId, invoice.objectId, isNativeInvoice || invoiceSourceDetail1 === IntegrationAppNames.QUICKBOOKS_ONLINE);
  const isDownloadable = quickBooksInvoiceDownloadUrl || hasQBOInvoicesSyncRecord;
  const downloadNonNativeInvoicePdf = useCallback(() => {
    if (isDownloadable) {
      http.get(`${quickBooksInvoiceDownloadUrl}/${invoice.objectId}`, {
        responseType: 'blob'
      }).then(pdfBlob => {
        executePDFDownload(pdfBlob, invoiceNumber || invoiceTitle);
      }).catch(error => {
        throw error;
      });
    }
  }, [invoice.objectId, invoiceNumber, invoiceTitle, isDownloadable, quickBooksInvoiceDownloadUrl]);
  const handleActionButtonClick = useCallback(key => {
    switch (key) {
      case actions.DELETE:
        trackUsage({
          action: 'Click delete invoice',
          category: getInvoiceCategories(invoice, paymentsEligibility)
        });
        setIsDeleteModalOpen(true);
        break;
      case actions.DOWNLOAD:
        trackUsage({
          action: 'Click download invoice',
          category: getInvoiceCategories(invoice, paymentsEligibility)
        });
        downloadNonNativeInvoicePdf();
        break;
      default:
        throw new Error(`handleActionButtonClick encountered unexpected action key. Received: ${key}`);
    }
  }, [downloadNonNativeInvoicePdf, invoice, paymentsEligibility]);
  const actionGroups = [[isDownloadable && /*#__PURE__*/_jsx(DownloadInvoiceButton, {
    onClickButton: handleActionButtonClick
  }, "downloadButton")], [hasInvoiceEditPermissions && canDeleteInvoice && /*#__PURE__*/_jsx(InvoiceDeleteButton, {
    disabled: isPaymentCollectedAutomatically || invoiceHasPayments,
    hasPayments: invoiceHasPayments,
    onClickButton: handleActionButtonClick
  }, "deleteButton")]];
  const availableActions = filterAndFlattenActionGroups(actionGroups);
  const availableActionsWithDividers = insertDividers(availableActions);
  if (availableActions.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Flex, {
      justifyContent: "end",
      children: /*#__PURE__*/_jsx(Flex, {
        children: /*#__PURE__*/_jsx(Dropdown, {
          "data-test-id": "invoice-highlight-actions-dropdown",
          menuWidth: 210,
          placement: "bottom right",
          buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoicesHighlightCard.buttons.actions"
          }),
          buttonUse: "transparent",
          children: /*#__PURE__*/_jsx(List, {
            children: availableActionsWithDividers
          })
        })
      })
    }), isDeleteModalOpen && /*#__PURE__*/_jsx(InvoiceDeleteModal, {
      invoice: invoice,
      loading: deleteLoading,
      onClose: () => setIsDeleteModalOpen(false),
      onConfirm: onConfirmDeleteInvoice
    })]
  });
};
export default NonNativeInvoiceHighlightActions;